import React, { useState } from 'react';
import {
  Package, Tag, Calendar, TrendingDown,
  CheckCircle2, X, Zap,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogOverlay, DialogPortal,
} from '@/components/ui/dialog';

const TYPE_META = {
  reorder:        { label: 'Reorder',    icon: Package,      badge: 'bg-blue-100 text-blue-700',    icon_cls: 'text-blue-600'   },
  dead_stock:     { label: 'Dead Stock', icon: Tag,          badge: 'bg-amber-100 text-amber-700',  icon_cls: 'text-amber-600'  },
  expiry:         { label: 'Expiry',     icon: Calendar,     badge: 'bg-red-100 text-red-700',      icon_cls: 'text-red-600'    },
  margin_leakage: { label: 'Margin',     icon: TrendingDown, badge: 'bg-orange-100 text-orange-700', icon_cls: 'text-orange-600' },
};

const PRIORITY_META = {
  critical: { label: 'Critical', cls: 'bg-red-100 text-red-700'       },
  high:     { label: 'High',     cls: 'bg-orange-100 text-orange-700' },
  medium:   { label: 'Medium',   cls: 'bg-blue-100 text-blue-700'     },
};

const fmt = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;

/**
 * SmartActionPopup
 * Props:
 *   open       – boolean
 *   action     – smart action object | null
 *   onClose    – () => void
 *   onAddress  – (id, notes) => Promise<void>
 *   onDismiss  – (id) => Promise<void>
 */
export default function SmartActionPopup({ open, action, onClose, onAddress, onDismiss }) {
  const [notes,      setNotes]      = useState('');
  const [addressing, setAddressing] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  // Reset notes when a new action is opened
  React.useEffect(() => { if (open) setNotes(''); }, [open, action?.id]);

  const handleAddress = async () => {
    if (!action) return;
    setAddressing(true);
    try {
      await onAddress(action.id, notes);
      onClose();
    } finally {
      setAddressing(false);
    }
  };

  const handleDismiss = async () => {
    if (!action) return;
    setDismissing(true);
    try {
      await onDismiss(action.id);
      onClose();
    } finally {
      setDismissing(false);
    }
  };

  const meta     = action ? (TYPE_META[action.type] || TYPE_META.reorder) : null;
  const Icon     = meta?.icon || Zap;
  const priority = action ? (PRIORITY_META[action.priority] || PRIORITY_META.medium) : null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogPortal>
        {/* Blurred overlay */}
        <DialogOverlay className="bg-black/40 backdrop-blur-sm" />

        <DialogContent className="max-w-md p-0 overflow-hidden gap-0">
          {/* Header bar */}
          <div className="px-5 pt-5 pb-4 border-b border-border">
            <DialogHeader>
              <div className="flex items-center gap-2 mb-2">
                {meta && (
                  <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${meta.badge}`}>
                    <Icon className="h-3 w-3" />
                    {meta.label}
                  </span>
                )}
                {priority && (
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${priority.cls}`}>
                    {priority.label}
                  </span>
                )}
                {action?.identified_value > 0 && (
                  <span className="ml-auto text-sm font-bold text-emerald-700">
                    {fmt(action.identified_value)}
                  </span>
                )}
              </div>
              <DialogTitle className="text-base font-semibold text-foreground leading-snug pr-6">
                {action?.headline || 'Smart Action'}
              </DialogTitle>
              {action?.product_name && (
                <p className="text-xs text-muted-foreground mt-0.5">{action.product_name}</p>
              )}
            </DialogHeader>
          </div>

          {/* Body */}
          {action ? (
            <div className="px-5 py-4 space-y-4">
              {/* Suggestion */}
              <p className="text-sm text-foreground/80 leading-relaxed">{action.suggestion}</p>

              {/* Notes */}
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Notes (optional)</label>
                <input
                  type="text"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleAddress(); }}
                  placeholder="e.g. Called supplier, placed order…"
                  className="w-full border border-border rounded-lg px-3 py-1.5 text-sm outline-none focus:border-primary"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-1">
                <button
                  onClick={handleAddress}
                  disabled={addressing || dismissing}
                  className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  {addressing ? 'Saving…' : 'Mark Addressed'}
                </button>
                <button
                  onClick={handleDismiss}
                  disabled={addressing || dismissing}
                  className="flex items-center gap-1.5 border border-border text-muted-foreground hover:text-foreground text-sm px-3 py-2 rounded-lg transition-colors disabled:opacity-50"
                >
                  <X className="h-4 w-4" />
                  {dismissing ? 'Skipping…' : 'Skip'}
                </button>
              </div>
            </div>
          ) : (
            <div className="px-5 py-8 text-center text-muted-foreground text-sm">
              No open smart action for this product.
            </div>
          )}
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
